import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Calendar, User, Wrench, Crown, Droplet, ClipboardCheck, TrendingUp, PiggyBank, Gift } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Widget = ({ icon: Icon, label, value, sub, accent, link, testid }) => {
  const inner = (
    <div data-testid={testid} className={`bg-[#141414] border border-[#262626] rounded-md p-5 h-full ${link ? 'hover:border-[#D4AF37] transition-colors cursor-pointer' : ''}`}>
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-md flex items-center justify-center">
          <Icon className="w-5 h-5 text-[#D4AF37]" />
        </div>
        <p className="text-[#A3A3A3] text-xs uppercase tracking-wider">{label}</p>
      </div>
      <p className={`text-xl font-bold ${accent || 'text-white'}`}>{value || '—'}</p>
      {sub && <p className="text-[#A3A3A3] text-xs mt-1">{sub}</p>}
    </div>
  );
  return link ? <Link to={link} className="block">{inner}</Link> : inner;
};

const CustomerDashboard = () => {
  const [d, setD] = useState(null);
  const [credit, setCredit] = useState(0);
  const [upcoming, setUpcoming] = useState([]);
  useEffect(() => {
    axios.get(`${API}/customer/dashboard`, { withCredentials: true }).then(({ data }) => setD(data)).catch(console.error);
    axios.get(`${API}/customer/credit-balance`, { withCredentials: true }).then(({ data }) => setCredit(data.balance || 0)).catch(() => setCredit(0));
    axios.get(`${API}/customer/me/upcoming-maintenance`, { withCredentials: true }).then(({ data }) => setUpcoming(data.upcoming || [])).catch(() => setUpcoming([]));
  }, []);
  if (!d) return <div className="text-[#A3A3A3]">Loading...</div>;

  return (
    <div data-testid="customer-dashboard-page" className="space-y-8">
      <div>
        <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2">Welcome Home</h2>
        <p className="text-[#A3A3A3]">Your plumbing care, all in one place.</p>
      </div>

      {credit > 0 && (
        <div data-testid="customer-credit-banner" className="bg-gradient-to-r from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/40 rounded-md p-5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
            <Gift className="w-6 h-6 text-[#D4AF37]" />
          </div>
          <div>
            <p className="text-[#D4AF37] text-xs font-bold uppercase tracking-wider">Account Credit Available</p>
            <p className="text-3xl font-bold text-white">${credit.toFixed(2)}</p>
            <p className="text-[#A3A3A3] text-xs mt-0.5">Will be automatically applied to your next paid invoice.</p>
          </div>
        </div>
      )}

      {upcoming.length > 0 && (
        <div data-testid="cust-upcoming-block" className="bg-[#141414] border border-[#D4AF37]/30 rounded-md p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[#D4AF37] text-xs font-bold uppercase tracking-wider">Upcoming Recommended Service</p>
            <Link to="/customer/service-request" className="text-[#D4AF37] text-xs hover:underline">Schedule Now →</Link>
          </div>
          <div className="space-y-2">
            {upcoming.slice(0, 4).map((u) => (
              <div key={u.id} data-testid={`cust-upcoming-${u.id}`} className="flex items-center justify-between gap-3 py-2 border-t border-[#262626] first:border-t-0">
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium truncate">{u.service_type || u.shift_title || 'Service Visit'}</p>
                  <p className="text-[#A3A3A3] text-xs">{u.date}{u.is_followup_draft ? ' · Draft — we\'ll confirm with you' : ''}</p>
                </div>
                <Link
                  to="/customer/service-request"
                  data-testid={`cust-upcoming-schedule-${u.id}`}
                  className="text-[10px] uppercase tracking-wider px-2.5 py-1 rounded bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-semibold flex-shrink-0"
                >Schedule</Link>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Widget icon={Calendar} label="Last Service Visit" value={d.last_visit?.date} sub={d.last_visit?.service_type} link="/customer/service-request" testid="cust-widget-last-visit" />
        <Widget icon={User} label="Last Technician" value={d.last_visit?.technician} link="/customer/service-request" testid="cust-widget-last-tech" />
        <Widget icon={Crown} label="Membership Status" value={d.membership ? `${d.membership.tier?.toUpperCase()} • ${d.membership.status?.toUpperCase()}` : 'Not a Member'} accent={d.membership ? 'text-[#D4AF37]' : 'text-white'} sub={d.membership?.discount_percent ? `Enjoying ${d.membership.discount_percent}% off all repairs` : null} link="/customer/membership" testid="cust-widget-membership" />
        <Widget icon={Droplet} label="Water Heater Flush Due" value={d.water_heater_flush_due} sub={d.water_heater_flush_due ? 'Recommended annually' : 'Schedule your first flush'} link="/customer/service-request" testid="cust-widget-flush" />
        <Widget icon={ClipboardCheck} label="Last Inspection" value={d.last_inspection_date} link="/customer/service-request" testid="cust-widget-inspection" />
        <Widget icon={Wrench} label="Upcoming Maintenance" value={d.upcoming_maintenance?.date} sub={d.upcoming_maintenance?.service_type} link="/customer/service-request" testid="cust-widget-upcoming" />
        <Widget icon={TrendingUp} label="Total Lifetime Spending" value={`$${(d.lifetime_spent || 0).toFixed(2)}`} accent="text-[#D4AF37]" link="/customer/spending" testid="cust-widget-lifetime" />
        <Widget icon={PiggyBank} label="Total Membership Savings" value={`$${(d.lifetime_savings || 0).toFixed(2)}`} accent="text-green-400" sub="Saved with your plan" link="/customer/membership" testid="cust-widget-savings" />
      </div>

      <div className="bg-[#141414] border border-[#262626] rounded-md p-6">
        <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em] uppercase mb-2">Our Promise to You</p>
        <p className="text-white text-sm">We show up on time. We do the job right. We treat your home like it's our own.</p>
      </div>
    </div>
  );
};

export default CustomerDashboard;
